import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { useHead } from "@/lib/head";
import { ArrowLeft, Flame, MessageCircle, Package, Phone, ShieldCheck, Tag, Truck, Warehouse } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/pellet-banner.jpg";

// Le due essenze corrispondono ai due prodotti effettivamente tenuti: conifera
// = WoodLife Tropical Heat, faggio = Gamauf Mammut. Non un elenco generico di
// essenze, ma la risposta alla domanda che il cliente fa davvero al banco:
// "quale dei due prendo?". Le differenze (cenere, durata) sono le caratteristiche
// generali di legno tenero e legno duro.
const CONFRONTO = [
  {
    essenza: "Conifera",
    prodotto: "WoodLife Tropical Heat",
    sintesi: "Meno cenere, meno pulizie",
    testo:
      "Il legno tenero brucia più pulito: il braciere si sporca meno e la stufa chiede manutenzione con meno frequenza. È la scelta comoda per l'uso domestico di tutti i giorni, e infatti è quella che gira di più.",
  },
  {
    essenza: "Faggio",
    prodotto: "Gamauf Mammut",
    sintesi: "Fiamma più lunga, più cenere",
    testo:
      "Il legno duro è più denso: a parità di carico la combustione dura di più, ma lascia più residuo nel braciere. Ha senso se tieni la stufa accesa a lungo e non ti pesa pulire più spesso.",
  },
];

// Limiti della certificazione ENplus A1 (norma ISO 17225-2): sono i valori che
// il marchio garantisce, non caratteristiche rivendicate sul nostro prodotto.
const ENPLUS = [
  { valore: "≤ 10%", voce: "Umidità", perche: "Pellet umido scalda meno e ingrassa la canna fumaria." },
  { valore: "≤ 0,7%", voce: "Ceneri", perche: "Meno residuo nel braciere, meno pulizie durante la stagione." },
  { valore: "≥ 16,5 MJ/kg", voce: "Potere calorifico", perche: "Circa 4,6 kWh per chilo: è il calore che paghi davvero." },
  { valore: "≥ 98%", voce: "Durabilità", perche: "Meno segatura nel sacco, la coclea si inceppa di meno." },
];

// Marche tenute in negozio. PREZZO: cambia durante la stagione, va riallineato
// qui e nello schema Product insieme.
//
// I valori di Tropical Heat (conifera, 15 kg, 84 sacchi/bancale, ceneri, umidita',
// potere calorifico) sono quelli dichiarati dal produttore su woodlifeitalia.com.
// Il bancale da 84 sacchi combacia con il dato del negozio: e' la conferma che la
// linea e' quella giusta. Ma la CLASSE della certificazione WoodLife non la
// esplicita: per Tropical Heat si dice solo "ENplus" finche' non si legge sul
// sacco. Gamauf invece dichiara ENplus A1 con codice HR011, quindi li' la classe
// si puo' scrivere. Il bancale da 78 sacchi da 15 kg e' il dato che conferma la
// marca, come l'84 per WoodLife.
const AGGIORNAMENTO_PREZZI = "agosto 2026";

const MARCHE = [
  {
    nome: "WoodLife Tropical Heat",
    ruolo: "La marca principale",
    prezzo: "7,50 € al sacco da 15 kg",
    bancale: "Bancale da 84 sacchi, 1.260 kg",
    testo:
      "Pellet di conifera certificato ENplus, ed è quello che gira di più da noi. I valori dichiarati dal produttore battono i minimi della classe A1 su tutta la linea: ceneri ≤ 0,4% contro 0,7%, umidità ≤ 7% contro 10%, potere calorifico ≥ 4,9 kWh/kg contro 4,6. Diametro 6 mm.",
  },
  {
    nome: "Gamauf Mammut",
    ruolo: "L'alternativa in faggio",
    prezzo: "Prezzo su richiesta",
    bancale: "Bancale da 78 sacchi, 1.170 kg",
    testo:
      "In negozio è quello con la busta blu. Pellet di faggio con piccole aggiunte di frassino e carpino, prodotto in Croazia da Gamauf e certificato ENplus A1, codice HR011. Per il prezzo aggiornato conviene una telefonata.",
  },
];

const PelletCombustibili = () => {
  useHead("prodotto-pellet", [
    {
      tag: "ldjson",
      data: {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": "https://rubinimagione.it/prodotti/pellet-combustibili#wood-life",
        name: "Pellet WoodLife Tropical Heat",
        brand: { "@type": "Brand", name: "WoodLife" },
        category: "Pellet per stufe",
        material: "Legno di conifera",
        weight: { "@type": "QuantitativeValue", value: 15, unitCode: "KGM" },
        description:
          "Pellet di conifera certificato ENplus in sacco da 15 kg, venduto singolo o a bancale da 84 sacchi (1.260 kg), con consegna a domicilio entro 30 km da Magione (PG).",
        offers: {
          "@type": "Offer",
          price: "7.50",
          priceCurrency: "EUR",
          // il prezzo del pellet si muove durante la stagione: la scadenza evita
          // che Google tenga per buono un valore vecchio a tempo indeterminato
          priceValidUntil: "2026-12-31",
          availability: "https://schema.org/InStock",
          url: "https://rubinimagione.it/prodotti/pellet-combustibili",
          seller: { "@id": "https://rubinimagione.it/#negozio" },
        },
      },
    },
  ]);

  return (
    <div className="min-h-screen">
      <SEO
        title="Vendita Pellet ENplus A1 e Legna da Ardere a Magione"
        description="Pellet WoodLife Tropical Heat di conifera e Gamauf Mammut ENplus A1 di faggio, a Magione (PG). Sacchi da 15 kg e bancali, consegna entro 30 km su Perugia."
        canonical="/prodotti/pellet-combustibili"
        type="product"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Pellet e Combustibili", url: "/prodotti/pellet-combustibili" },
        ]}
      />
      <Navbar />

      {/* Hero. Altezza guidata dal contenuto e non fissa come sulle altre pagine
          prodotto: qui sotto il testo ci sono due CTA, e con h-[450px] su mobile
          finivano tagliate fuori dall'overflow-hidden. */}
      <section className="relative overflow-hidden pt-28 pb-14 md:pt-36 md:pb-20 md:min-h-[500px] flex items-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bannerImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        <div className="relative container mx-auto px-4 w-full flex flex-col justify-center">
          <Link to="/#prodotti" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai prodotti
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Flame className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Pellet per stufe e legna da ardere a Magione
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Pellet di conifera e di faggio in sacchi da 15 kg e bancali, e legna da ardere.
              Consegna a domicilio entro 30 km, su Perugia e su tutto il lago Trasimeno.
            </p>

            {/* CTA in alto: chi arriva da una ricerca sul pellet vuole prenotare
                subito, non dopo aver scrollato tutta la pagina. */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button
                asChild
                size="lg"
                className="btn-cta rounded-full px-8 py-6 text-base font-semibold uppercase"
                style={{ fontFamily: "Verdana, sans-serif" }}
              >
                <a href="tel:+393466257639">
                  <Phone className="w-4 h-4 mr-2" />
                  Prenota il pellet
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-semibold uppercase bg-white/10 text-white border-white/40 hover:bg-white/20 backdrop-blur-sm"
                style={{ fontFamily: "Verdana, sans-serif" }}
              >
                <a href="https://wa.me/393466257639" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Marche e prezzi */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
              <Tag className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Le marche che teniamo</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Due marche certificate ENplus, una di conifera e una di faggio, entrambe a sacco singolo o a bancale intero.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {MARCHE.map((m) => (
              <div key={m.nome} className="bg-card rounded-2xl p-8 border border-border card-hover">
                <span className="text-sm text-primary font-medium">{m.ruolo}</span>
                <h3 className="text-2xl font-heading font-semibold mt-1 mb-4">{m.nome}</h3>
                <p className="text-3xl font-heading font-bold mb-1">{m.prezzo}</p>
                <p className="text-muted-foreground mb-4">{m.bancale}</p>
                <p className="text-muted-foreground">{m.testo}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a
              href="tel:+393466257639"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              <Phone className="w-4 h-4" />
              Prenota o chiedi il prezzo del bancale
            </a>
            <a
              href="https://wa.me/393466257639"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Scrivici su WhatsApp
            </a>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto">
            Prezzi aggiornati ad {AGGIORNAMENTO_PREZZI}. Il pellet è una materia prima e il prezzo si muove
            durante la stagione: per il prezzo del giorno, e per quello del bancale intero, chiamaci.
          </p>
        </div>
      </section>

      {/* Tipi di pellet */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Conifera o faggio, quale dei due</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              È la differenza vera fra i due pellet che teniamo, e non è una questione di qualità:
              sono entrambi certificati. Cambia come si comportano nella stufa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {CONFRONTO.map((c) => (
              <div key={c.essenza} className="bg-card rounded-2xl p-8 border border-border card-hover">
                <div className="flex items-baseline justify-between mb-1 gap-4">
                  <h3 className="text-2xl font-heading font-semibold">{c.essenza}</h3>
                  <span className="text-sm text-primary font-medium flex-shrink-0">{c.sintesi}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{c.prodotto}</p>
                <p className="text-muted-foreground">{c.testo}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-muted-foreground mt-10 max-w-2xl mx-auto">
            Se hai dubbi portaci la marca e il modello della stufa: alcune si trovano meglio con un'essenza
            che con l'altra, e in negozio ti diciamo cosa conviene nel tuo caso.
          </p>
        </div>
      </section>

      {/* ENplus A1 */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Cosa garantisce la sigla ENplus A1</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              È la certificazione più severa per il pellet domestico. Non è un bollino di marketing:
              impone dei limiti misurati, ed è il motivo per cui due sacchi allo stesso prezzo possono
              rendere in modo molto diverso.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {ENPLUS.map((r) => (
              <div key={r.voce} className="bg-card rounded-2xl p-6 border border-border">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-2xl font-heading font-bold text-primary">{r.valore}</span>
                  <span className="font-semibold">{r.voce}</span>
                </div>
                <p className="text-sm text-muted-foreground">{r.perche}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quanto ne serve */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-center">
              Quanto pellet serve per una stagione
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Non esiste un numero valido per tutti, e chi te lo dà al telefono senza chiederti niente
              sta tirando a indovinare. Il consumo dipende da quattro cose: quanti metri quadri scaldi,
              quanto è isolata la casa, se hai una stufa in una stanza o una caldaia che serve tutto
              l'impianto, e quante ore al giorno la tieni accesa.
            </p>
            <p className="text-lg text-muted-foreground mb-6">
              Per dare un ordine di grandezza: una stufa che scalda il piano giorno di una casa media,
              accesa nelle ore fredde, consuma indicativamente <strong>fra i 15 e i 30 quintali</strong> da
              novembre a marzo. Una caldaia a pellet che copre l'intera abitazione può superare
              tranquillamente quella cifra.
            </p>
            <p className="text-lg text-muted-foreground">
              Il modo serio di capirlo è guardare quanto hai consumato l'anno scorso. Se è il primo
              inverno, passa in negozio con i dati della stufa: si fa un conto insieme e si parte con una
              quantità prudente, che poi si integra.
            </p>
          </div>
        </div>
      </section>

      {/* Conservazione e stagione */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border border-border">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-muted rounded-full mb-4">
                <Warehouse className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">Come conservarlo</h3>
              <p className="text-muted-foreground">
                Al chiuso e sollevato da terra, su un bancale o due assi: il pellet assorbe umidità dal
                pavimento anche attraverso il sacco, e una volta gonfio rende meno e sbriciola. Un garage
                asciutto va benissimo, una tettoia aperta no.
              </p>
            </div>

            <div className="bg-card rounded-2xl p-8 border border-border">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-muted rounded-full mb-4">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">Quando conviene comprarlo</h3>
              <p className="text-muted-foreground">
                In estate e a inizio autunno, prima che parta la stagione. Quando arrivano i primi freddi
                la domanda si concentra tutta insieme e la disponibilità delle essenze migliori si
                assottiglia. Chi si muove ad agosto sceglie, chi si muove a dicembre prende quel che c'è.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Legna */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-heading font-bold mb-4">Legna da ardere</h2>
            <p className="text-lg text-muted-foreground">
              Oltre al pellet trovi legna da ardere per camini, stufe e forni. Per quantità e disponibilità
              del momento conviene una telefonata: la legna non è un prodotto sempre uguale, dipende da
              cosa è arrivato e da quanto è stagionato.
            </p>
          </div>
        </div>
      </section>

      {/* Consegna */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <Truck className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-3xl font-heading font-bold mb-4">Te lo portiamo noi</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Sacchi e bancali interi consegnati a domicilio entro 30 km da Magione, con il nostro mezzo.
            È il prodotto per cui la consegna conviene di più: sono decine di chili che non devi caricare
            e scaricare da solo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consegne"
              className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              Dove consegniamo
            </Link>
            <a
              href="tel:+393466257639"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Phone className="w-4 h-4" />
              Chiedi il prezzo
            </a>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default PelletCombustibili;
